/**
 * The one way a surface changes a deal's stage.
 *
 * The previous pattern (`useChangeDealStatus`) wrote the PERSISTED Zustand
 * store optimistically and then fired `.mutate()` with no rollback, so a failed
 * server write left local and database disagreeing — and the divergence
 * survived a restart, because the store is persisted. It resolved only if a
 * later server merge happened to overwrite it.
 *
 * Illegal transitions are refused here as well as server-side: the UI cannot
 * offer one, but a stale client must not be able to force one either.
 *
 * The decisions live in `stage-change.ts` (a leaf module) and the writes live
 * here, so `planStageChange` and `hasServerRow` are testable — importing THIS
 * file pulls in `@/lib/errors` → `@sentry/react-native`, which jest cannot
 * transform.
 */
import { useCallback } from 'react';

import { handleError } from '@/lib/errors';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useUpdateDealflowStatus } from '@/lib/supabase';
import { hasServerRow, planStageChange } from './stage-change';
import { isTerminal, type DealStage } from './stage';
import { rpc } from '@/lib/supabase/rpc';
import { reportError } from '@/lib/errors';

/**
 * Store write + server write + rollback, in one place.
 *
 * Private: every caller goes through one of the two named hooks below, so a
 * future surface cannot acquire an ungated stage write by importing this.
 */
function useStageWrite() {
  const updateLocally = useDealflowStore((s) => s.updateDealflowStatus);
  const updateOnServer = useUpdateDealflowStatus();

  return useCallback(
    ({ dealId, next, rollbackTo }: { dealId: string; next: DealStage; rollbackTo: DealStage }) => {
      updateLocally(dealId, next);

      // A locally-analysed deal has no row to update, so there is no write that
      // could fail and nothing to roll back. Firing one anyway would bounce the
      // card and show an error on a path that works — see `hasServerRow`.
      if (!hasServerRow(dealId)) return;

      updateOnServer.mutate(
        { dealflowId: dealId, status: next },
        {
          onError: (err) => {
            // Put the card back where it was. Without this the persisted store
            // keeps a stage the database rejected, across restarts.
            updateLocally(dealId, rollbackTo);
            handleError(err, { context: 'useChangeStage.mutate' });
          },
          onSuccess: () => {
            // Standing down: a deal at a terminal stage is one the investor has
            // stopped pursuing, so a still-pending data-room request on it is
            // now an unanswerable question sitting in a founder's queue.
            //
            // Found in production: two requests pending for days after the
            // investor passed or moved to monitoring, with no way for either
            // side to clear them — the investor has no UPDATE or DELETE policy
            // on the table, only the startup side does.
            //
            // AFTER the stage write succeeds, never before: withdrawing a
            // request for a move the database then refused would close a
            // question the investor is still asking.
            if (isTerminal(next)) withdrawPendingRequest(dealId);
          },
        },
      );
    },
    [updateLocally, updateOnServer],
  );
}

/**
 * Close a still-pending data-room request on a deal the investor has finished
 * with.
 *
 * Fire-and-forget by design. The stage change is the user's action and has
 * already succeeded; failing it because the tidy-up failed would be worse than
 * leaving one stale request. The RPC is a no-op returning false when the
 * founder has already answered, so this never needs to check first.
 *
 * Goes through `withdraw_data_room_request` rather than a direct UPDATE: RLS
 * cannot restrict which columns an UPDATE touches, so a policy permissive
 * enough for this would also let an investor write their own `status` — and an
 * 'approved' request reads as granted access on the investor's own screens.
 */
function withdrawPendingRequest(dealId: string): void {
  const deal = useDealflowStore.getState().dealflow.find((d) => d.id === dealId);
  const startupId = deal?.startup?.id;
  if (!startupId) return;

  void rpc('withdraw_data_room_request', { p_startup_id: startupId }).then(({ error }) => {
    // supabase-js reports failure via `{ error }` rather than throwing, so an
    // unchecked result here would be a silent no-op (ARCH-06).
    if (error) {
      reportError(error, {
        context: 'useChangeStage.withdrawPendingRequest',
        level: 'warning',
        extra: { startupId },
      });
    }
  });
}

/**
 * THE stage change a surface performs: `changeStage(dealId, from, to)`.
 *
 * `from` is where the card is now, and it is load-bearing twice over — it is
 * what the legality check reads, and it is where a rejected write puts the card
 * back. Pass the deal's real stage; never a guess.
 *
 * Returns whether the move was APPLIED, so a caller can avoid reporting a
 * change that did not happen. A card never needs this — it offers only the
 * actions `offeredActions` derives, so its target is legal by construction —
 * but a BULK action does: one selection spans stages, and some of them will
 * refuse. Reporting `dealflow_status_changed` for those would put moves that
 * never occurred into the analytics.
 */
export function useChangeStage() {
  const write = useStageWrite();

  return useCallback(
    (dealId: string, from: DealStage, to: DealStage): boolean => {
      const plan = planStageChange(dealId, from, to);
      if (!plan.ok) return false;
      write(plan);
      return true;
    },
    [write],
  );
}

/*
 * There used to be a second export here, `useMoveToAwaitingAccess` — the ONE
 * stage write that skipped `planStageChange`'s legality gate. It existed
 * because `startup-detail.tsx` offered the data-room request at `screening`
 * while `canTransition('screening','awaiting_access')` was false, so routing it
 * through the gate would have written the founder's request row and then
 * refused the stage move, stranding the deal.
 *
 * The owner settled the contradiction the other way (2026-08-06): the
 * transition is legal now, `screening` offers `request-access`, and the
 * exception has no reason to exist. `deal-actions.ts` calls `useChangeStage`
 * like everything else — which is what this module's header claimed all along.
 * Deleted rather than narrowed: an ungated write kept "for now" is how the
 * next surface acquires one.
 */
